import { getPartialDateGivenMonthsAgo, isNumber, nowPartialDate } from 'neuro-utils';
import { formatRegExpArray, partialDateToString } from '../utils/queryFormatters';
import { TPlatformHandler } from '.';
import { capitalize } from 'Utility/string';
import { TExaminationType, TSleepApneaESPapSettingsType } from '../../definitions/sleepApnea';

const makeCaseInsensitiveRegExpString = (input: string): string => {
  let insensitivePattern = '';
  for (const char of input) {
    // Check if the character is an alphabet letter
    if (/[a-zA-Z]/.test(char)) {
      insensitivePattern += `[${char.toLowerCase()}${char.toUpperCase()}]`;
    } else {
      // If it's not a letter (e.g. ^, $, ?, ., *, digits), keep it as is
      insensitivePattern += char;
    }
  }
  return insensitivePattern;
};

const getMonthsNumberFromTimeFrame = (opt: NonNullable<TSleepApneaESPapSettingsType['timeframe']>): number =>
  Number(opt.replace(/\D/g, ''));

export const handleSleepApneaField: TPlatformHandler = (field, state) => {
  let query: string | undefined = undefined;
  switch (field) {
    case 'examinations': {
      query = '';
      if (state.examinations !== 'noCropping' && Array.isArray(state.examinations)) {
        const codeMap: Record<string, string> = {
          'cB-pH': '1528',
          'cB-pCO2': '1622',
          'cB-pO2': '1536',
          'cB-HCO3-st': '154',
          'cB-BE': '151',
        };
        const parts = state.examinations
          .filter((e): e is TExaminationType & { codeName: NonNullable<TExaminationType['codeName']> } =>
            Boolean(e.codeName),
          )
          .map(e => {
            let part = `examination@_€{data.code€{exists==TRUE&data==$${codeMap[e.codeName]}}`;
            if (e.value && e.value !== 'noCropping') {
              part += '&data.value€{exists==TRUE';
              if (isNumber(e.value.minValue)) part += `&data>=#${e.value.minValue}`;
              if (isNumber(e.value.maxValue)) part += `&data<=#${e.value.maxValue}`;
              part += '}';
            }
            part += '}';
            return part;
          });
        if (parts.length > 0) query = parts.length > 1 ? `(${parts.join('&')})` : parts[0];
      }
      break;
    }

    case 'professionalDriving':
      query = 'background@professionalDriving==$yes';
      break;

    case 'treatment':
      query = '';
      if (state.treatment !== 'noCropping') {
        state.treatment.forEach((t, i, arr) => {
          // Ignore empty search criteria
          if (!Array.isArray(t.treatment)) return;
          query += '(';
          t.treatment.forEach((treatment, i, arr) => {
            query += '(';
            const treatmentType = treatment === 'postureTreatment' ? 'otherTreatment' : treatment;
            // Start date is earlier than / same as current date
            query += `${treatmentType}@_€{data.date<=%${partialDateToString(nowPartialDate())}`;
            // AND otherTreatmentType is postureTreatment IF treatmentType is otherTreatment
            if (treatmentType === 'otherTreatment') {
              query += `&data.otherTreatmentType==$postureTreatment`;
            }
            if (t.status === 'ongoing') {
              // AND treatment has not ended
              query += `&data.hasEnded€{exists==FALSE|data==NULL|data.0==FALSE|data.length==#0}`;
              // AND end date does not exist OR it is later than current date
              query += `&data.endDate€{exists==FALSE|data==NULL|data>%${partialDateToString(nowPartialDate())}}`;
            }
            if (t.status === 'ended') {
              // AND treatment has ended IF its type is not patientDoesNotWantRespiratorySupportTherapy
              if (treatmentType !== 'patientDoesNotWantRespiratorySupportTherapy') {
                query += `&data.hasEnded€{exists==TRUE&data.0==TRUE}`;
              }
              // AND end date exists AND it is earlier than current date
              query += `&data.endDate€{exists==TRUE&data<%${partialDateToString(nowPartialDate())}}`;
            }
            query += '}';
            query += `)${i < arr.length - 1 ? '|' : ''}`;
          });
          const arrNext = arr[i + 1];
          query += `)${arrNext && Array.isArray(arrNext.treatment) && arrNext.treatment.length > 0 ? '&' : ''}`;
          if (i === arr.length - 1) query = `(${query})`;
        });
      }
      break;

    case 'papDevice': {
      query = '';
      const { papDevice } = state;
      if (papDevice !== 'noCropping' && papDevice.papDeviceType) {
        query += `§getResMedDeviceSummary[%"${partialDateToString(nowPartialDate())}"]€{data.latest_device_type_desc==/"^${makeCaseInsensitiveRegExpString(papDevice.papDeviceType)}$"`;

        if (papDevice.papDeviceMode && papDevice.papDeviceMode !== 'noCropping') {
          query += `&(${formatRegExpArray(
            'latest_device_mode',
            papDevice.papDeviceMode.map(mode => `^${makeCaseInsensitiveRegExpString(mode)}$`),
          )})`;
        }

        if (papDevice.durationWeeks && papDevice.durationWeeks !== 'noCropping') {
          const parts: string[] = [];

          if (isNumber(papDevice.durationWeeks.minDurationWeeks)) {
            parts.push(`data.therapy_duration_weeks>=#${papDevice.durationWeeks.minDurationWeeks}`);
          }

          if (isNumber(papDevice.durationWeeks.maxDurationWeeks)) {
            parts.push(`data.therapy_duration_weeks<=#${papDevice.durationWeeks.maxDurationWeeks}`);
          }

          query += `&(${parts.join('&')})`;
        }

        query += '}';
      }
      break;
    }
    case 'papTreatmentResponse': {
      query = '';
      const { papTreatmentResponse } = state;
      if (papTreatmentResponse !== 'noCropping' && papTreatmentResponse.timeframe) {
        const fieldNameToColumnNameMap = new Map<keyof typeof papTreatmentResponse, string>([
          ['averagePapDeviceUsage', 'avg_usage_hours'],
          ['averageResidueAhi', 'avg_ahi'],
          ['spikeResidueAhi', 'max_rolling_avg_ahi'],
          ['averageCai', 'avg_cai'],
          ['averageMaskLeakage', 'avg_mask_leak'],
        ]);

        if (
          !fieldNameToColumnNameMap
            .keys()
            .some(f => papTreatmentResponse[f] && papTreatmentResponse[f] !== 'noCropping')
        ) {
          return query;
        }

        const months = getMonthsNumberFromTimeFrame(papTreatmentResponse.timeframe);

        const rollingAverageWindow =
          papTreatmentResponse.spikeResidueAhi && papTreatmentResponse.spikeResidueAhi !== 'noCropping'
            ? (papTreatmentResponse.spikeResidueAhi.rollingAverageWindow ?? 1)
            : 1;

        query += `§getResMedTreatmentResponseSummary[%"${partialDateToString(getPartialDateGivenMonthsAgo(months))}",%"${partialDateToString(nowPartialDate())}",#${rollingAverageWindow}]€{`;

        fieldNameToColumnNameMap
          .keys()
          .filter(field => typeof papTreatmentResponse[field] === 'object')
          .forEach((field, index) => {
            const parentField = papTreatmentResponse[field] as { [key: string]: number | undefined };
            const column = fieldNameToColumnNameMap.get(field);
            if (index > 0) query += '&';
            switch (field) {
              case 'spikeResidueAhi':
                if ('rollingAverageThreshold' in parentField && isNumber(parentField.rollingAverageThreshold)) {
                  query += `data.${column}>#${parentField.rollingAverageThreshold}`;
                }
                break;
              default: {
                const parts: string[] = [];
                const minField = `min${capitalize(field)}`;
                const maxField = `max${capitalize(field)}`;

                if (isNumber(parentField[minField])) {
                  parts.push(`data.${column}>=#${parentField[minField]}`);
                }

                if (isNumber(parentField[maxField])) {
                  parts.push(`data.${column}<=#${parentField[maxField]}`);
                }

                query += `(${parts.join('&')})`;
                break;
              }
            }
          });

        query += '}';
      }
      break;
    }

    case 'papSettings': {
      query = '';
      const { papSettings } = state;
      if (papSettings !== 'noCropping' && papSettings.timeframe) {
        const fieldNameToColumnNamesMap = new Map<keyof typeof papSettings, [string, string]>([
          ['ipap', ['min_ipap', 'max_ipap']],
          ['epap', ['min_epap', 'max_epap']],
          ['pressureSupport', ['min_pressure_support', 'max_pressure_support']],
          ['respiratoryRate', ['min_respiratory_rate', 'max_respiratory_rate']],
        ]);

        if (!fieldNameToColumnNamesMap.keys().some(f => papSettings[f] && papSettings[f] !== 'noCropping')) {
          return query;
        }

        const months = getMonthsNumberFromTimeFrame(papSettings.timeframe);

        query += `§getResMedSettingsSummary[%"${partialDateToString(getPartialDateGivenMonthsAgo(months))}",%"${partialDateToString(nowPartialDate())}"]€{`;

        fieldNameToColumnNamesMap
          .keys()
          .filter(field => typeof papSettings[field] === 'object')
          .forEach((field, index) => {
            const parentField = papSettings[field] as { [key: string]: number | undefined };
            const columns = fieldNameToColumnNamesMap.get(field)!;

            if (index > 0) query += '&';

            const parts: string[] = [];
            const minField = `min${field === 'respiratoryRate' ? capitalize(field) : field.toUpperCase()}`;
            const maxField = `max${field === 'respiratoryRate' ? capitalize(field) : field.toUpperCase()}`;

            if (isNumber(parentField[minField])) {
              parts.push(`data.${columns[0]}>=#${parentField[minField]}`);
            }

            if (isNumber(parentField[maxField])) {
              parts.push(`data.${columns[1]}<=#${parentField[maxField]}`);
            }

            query += `(${parts.join('&')})`;
          });

        query += '}';
      }
      break;
    }

    case 'latestBMI':
      query = '';
      if (state.latestBMI !== 'noCropping' && (state.latestBMI.minBMI || state.latestBMI.maxBMI)) {
        query += '§calculateLatestBMI€{';
        if (state.latestBMI.minBMI || state.latestBMI.minBMI === 0) {
          query += `data!=NULL&data>=#${state.latestBMI.minBMI}`;
          if (state.latestBMI.maxBMI) query += '&';
        }
        if (state.latestBMI.maxBMI) query += `data<=#${state.latestBMI.maxBMI}`;
        query += '}';
      }
      break;

    case 'preDiagnosisESS': {
      query = '';
      const minField = 'minPreDiagnosisESS';
      const maxField = 'maxPreDiagnosisESS';
      if (
        state.preDiagnosisESS !== 'noCropping' &&
        (isNumber(state.preDiagnosisESS[minField]) || isNumber(state.preDiagnosisESS[maxField]))
      ) {
        const parts: string[] = [];
        if (isNumber(state.preDiagnosisESS[minField])) {
          parts.push(`data!=NULL&data>=#${state.preDiagnosisESS[minField]}`);
        }
        if (isNumber(state.preDiagnosisESS[maxField])) {
          parts.push(`data<=#${state.preDiagnosisESS[maxField]}`);
        }
        query = `§calculatePreDiagnosisESS€{${parts.join('&')}}`;
      }
      break;
    }

    case 'latestESS':
    case 'latestESSDelta': {
      query = '';

      const stateValue = state[field] as 'noCropping' | Record<string, unknown>;
      const func = {
        latestESS: 'calculateLatestESS',
        latestESSDelta: 'calculateLatestESSDelta',
      }[field];

      const minField = `min${capitalize(field)}`;
      const maxField = `max${capitalize(field)}`;

      if (stateValue !== 'noCropping' && (isNumber(stateValue[minField]) || isNumber(stateValue[maxField]))) {
        const parts: string[] = [];
        if (isNumber(stateValue[minField])) parts.push(`data!=NULL&data>=#${stateValue[minField]}`);
        if (isNumber(stateValue[maxField])) parts.push(`data<=#${stateValue[maxField]}`);
        query = `§${func}€{${parts.join('&')}}`;
      }
      break;
    }

    case 'tirednessOrNearMissWhileDrivingDuringLast12Months': {
      query = '';
      const tirednessField = state.tirednessOrNearMissWhileDrivingDuringLast12Months;
      if (tirednessField !== 'noCropping') {
        const dateStr = partialDateToString(getPartialDateGivenMonthsAgo(12));
        const parts: string[] = [];

        if (tirednessField.sleepApneaFirstVisitInquiry) {
          if (tirednessField.sleepApneaFirstVisitInquiry === 'oftenOrSometimes') {
            parts.push(
              `sleepApneaFirstVisitInquiry@_€{data.date>=%${dateStr}&(data.tirednessOrNearMissWhileDriving==$often|data.tirednessOrNearMissWhileDriving==$sometimes)}`,
            );
          } else if (['often', 'sometimes', 'never'].includes(tirednessField.sleepApneaFirstVisitInquiry)) {
            parts.push(
              `sleepApneaFirstVisitInquiry@_€{data.date>=%${dateStr}&data.tirednessOrNearMissWhileDriving==$${tirednessField.sleepApneaFirstVisitInquiry}}`,
            );
          }
        }

        if (tirednessField.deviceTherapyControlVisitInquiry) {
          parts.push(
            `deviceTherapyControlVisitInquiry@_€{data.date>=%${dateStr}&data.tirednessOrNearMissWhileDriving==$${tirednessField.deviceTherapyControlVisitInquiry}}`,
          );
        }

        if (parts.length > 1) query = `(${parts.join('|')})`;
        else if (parts.length === 1) query = parts[0];
      }
      break;
    }

    case 'weightGain': {
      query = '';
      const weightGainField = state.weightGain;
      if (weightGainField !== 'noCropping') {
        const threshold = weightGainField.threshold;
        // Can do a simple check since falsy 0 is not a valid input
        const months = weightGainField.minDurationYears && weightGainField.minDurationYears * 12;

        // Same applies here
        if (threshold && months) {
          // ETL should map code and value to a name-value pair
          query += `§calculateLatestFieldValueTrend[$measurement,$weight,$weight,$date,%"${partialDateToString(
            getPartialDateGivenMonthsAgo(months),
          )}"]>#${threshold}`;
        }
      }
      break;
    }

    default:
      return undefined;
  }

  return query;
};
